import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CircleNotch, FileText } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DocumentRenderer } from "@/components/documents/DocumentRenderer";
import { publicDocumentQueryOptions } from "@/api-queries/documents";
import { formatDate } from "@/lib/format";
import type { EnrollFormData, UpdateField } from "./types";
import "../blog/editor.css";

/**
 * Ugovor o poslovnoj saradnji u prijavi terapeuta. Tekst se čita iz aktivnog
 * `ugovor_terapeut` dokumenta, nikad iz koda. Ovdje se ugovor samo čita i
 * potvrđuje upoznatost; formalno prihvatanje (vezano za verziju) se bilježi
 * u onboardingu nakon odobrenja (`src/api/therapists/contract.ts`), pa ova
 * kvačica nije ništa što se šalje serveru.
 */
export function ContractConsent({
  form,
  updateField,
}: {
  form: EnrollFormData;
  updateField: UpdateField;
}) {
  const [open, setOpen] = useState(false);
  const { data: contract, isLoading, isError } = useQuery(publicDocumentQueryOptions("ugovor_terapeut"));

  return (
    <div className="rounded-xl border bg-muted/20 p-4">
      <Label className="cursor-pointer items-start gap-3 font-normal">
        <Checkbox
          className="mt-0.5"
          checked={form.agreeToContract}
          onCheckedChange={(c) => updateField("agreeToContract", c === true)}
        />
        <span className="text-sm leading-relaxed text-foreground">
          Upoznat/a sam sa sadržajem Ugovora o poslovnoj saradnji i saglasan/na sam da ga
          prihvatim nakon odobrenja prijave.
        </span>
      </Label>

      <p className="mt-2 pl-8 text-xs text-muted-foreground">
        Ugovor prihvatate u aplikaciji pri prvom prijavljivanju nakon što tim odobri vašu
        prijavu. Slanjem prijave ne nastaje ugovorna obaveza.
      </p>

      <div className="mt-3 pl-8">
        {isError ? (
          <p className="text-xs text-muted-foreground">
            Tekst ugovora trenutno nije dostupan online. Bit će vam dostavljen prije potpisivanja.
          </p>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isLoading}
            onClick={() => setOpen(true)}
          >
            {isLoading ? (
              <CircleNotch className="size-4 animate-spin" />
            ) : (
              <FileText className="size-4" />
            )}
            Pročitaj ugovor
          </Button>
        )}
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} className="sm:max-w-3xl">
        <DialogHeader onClose={() => setOpen(false)}>
          <DialogTitle>{contract?.title ?? "Ugovor o poslovnoj saradnji"}</DialogTitle>
          {contract && (
            <p className="mt-1 text-xs text-muted-foreground">
              Verzija {contract.version}, ažurirano {formatDate(contract.updatedAt)}
            </p>
          )}
        </DialogHeader>
        <DialogBody>
          {contract ? (
            <DocumentRenderer content={contract.contentJson} className="ProseMirror text-sm" />
          ) : (
            <div className="flex justify-center py-10">
              <CircleNotch className="size-6 animate-spin text-muted-foreground" />
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            Zatvori
          </Button>
          <Button
            type="button"
            onClick={() => {
              updateField("agreeToContract", true);
              setOpen(false);
            }}
          >
            Pročitao/la sam i slažem se
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
